import { FormEvent, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useChurch } from '../hooks/useChurches'
import {
  useChurchMembers,
  useCreateInvite,
  useRemoveMember,
  useUpdateMemberRole,
  useCancelInvite,
  useResendInvite,
} from '../hooks/useChurchAdmin'
import { useAuthStore } from '../store/auth'
import { AdminShell } from '../components/admin/AdminShell'
import { AdminPageHeader, AdminCard, AdmBtn, FormField, TextInput } from '../components/admin/AdminAtoms'

export default function MembersPage() {
  const { slug } = useParams<{ slug: string }>()
  const { user } = useAuthStore()

  const { data: churchData, isLoading: churchLoading } = useChurch(slug)
  const church = churchData?.data
  const { data: membersData } = useChurchMembers(church?.id)
  const createInvite = useCreateInvite(church?.id ?? '')
  const removeMember = useRemoveMember(church?.id ?? '')
  const updateRole = useUpdateMemberRole(church?.id ?? '')
  const cancelInvite = useCancelInvite(church?.id ?? '')
  const resendInvite = useResendInvite(church?.id ?? '')

  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<'coordinator' | 'viewer'>('coordinator')
  const [inviteSent, setInviteSent] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [resentId, setResentId] = useState<string | null>(null)

  const members = membersData?.data.members ?? []
  const pendingInvites = membersData?.data.pendingInvites ?? []

  const isOwner = members.some((m) => m.userId === user?.id && m.role === 'owner')

  async function handleInvite(e: FormEvent) {
    e.preventDefault()
    if (!inviteEmail.trim()) return
    setInviteError(null)
    setInviteSent(false)
    createInvite.mutate(
      { email: inviteEmail.trim(), role: inviteRole },
      {
        onSuccess: () => { setInviteEmail(''); setInviteSent(true) },
        onError: (err) => { setInviteError(err instanceof Error ? err.message : 'Failed to send invite') },
      },
    )
  }

  if (churchLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="text-[13px] text-muted">Loading…</div>
      </div>
    )
  }

  if (!church) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <div className="text-[13px] text-muted">Church not found.</div>
      </div>
    )
  }

  return (
    <AdminShell>
      <AdminPageHeader
        eyebrow={`${church.name} · team`}
        title="Members"
        sub={`${members.length} member${members.length !== 1 ? 's' : ''}${pendingInvites.length > 0 ? ` · ${pendingInvites.length} pending invite${pendingInvites.length !== 1 ? 's' : ''}` : ''}`}
      />

      <div className="grid gap-5" style={{ gridTemplateColumns: '1.2fr 1fr' }}>
        {/* Member list */}
        <AdminCard title="Team members">
          <div className="flex flex-col gap-2">
            {members.map((member) => {
              const isSelf = member.userId === user?.id
              return (
                <div
                  key={member.id}
                  className="flex items-center justify-between px-4 py-3 rounded-xl bg-s2 border border-line-soft gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-[13.5px] font-medium truncate">
                      {member.user?.displayName ?? member.user?.email}
                      {isSelf && <span className="text-[11px] text-muted ml-1.5">(you)</span>}
                    </div>
                    <div className="text-[11.5px] text-muted mt-0.5">{member.user?.email}</div>
                  </div>

                  {isOwner && !isSelf && member.role !== 'owner' ? (
                    <div className="flex items-center gap-2 shrink-0">
                      <select
                        value={member.role}
                        aria-label={`Role for ${member.user?.displayName ?? member.user?.email}`}
                        disabled={updateRole.isPending}
                        onChange={(e) =>
                          updateRole.mutate({ memberId: member.id, role: e.target.value as 'coordinator' | 'viewer' })
                        }
                        className="text-[11.5px] px-2 py-1 rounded-lg border border-line-soft bg-surface text-ink-2 capitalize"
                      >
                        <option value="coordinator">Coordinator</option>
                        <option value="viewer">Viewer</option>
                      </select>
                      <button
                        type="button"
                        aria-label={`Remove ${member.user?.displayName ?? member.user?.email}`}
                        disabled={removeMember.isPending}
                        onClick={() => removeMember.mutate(member.id)}
                        className="text-[11.5px] text-danger hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <span className="text-[10.5px] px-2.5 py-[3px] rounded-full bg-surface border border-line-soft text-ink-2 capitalize font-medium shrink-0">
                      {member.role}
                    </span>
                  )}
                </div>
              )
            })}

            {pendingInvites.length > 0 && (
              <>
                <div className="text-[11px] font-semibold text-muted uppercase tracking-[0.06em] mt-2 mb-0.5 px-1">
                  Pending invites
                </div>
                {pendingInvites.map((invite) => (
                  <div
                    key={invite.id}
                    className="flex items-center justify-between px-4 py-3 rounded-xl bg-s2 border border-line-soft opacity-70 gap-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-[13.5px] font-medium truncate">{invite.invitedEmail}</div>
                      <div className="text-[11.5px] text-muted mt-0.5">Invite pending</div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10.5px] px-2.5 py-[3px] rounded-full capitalize font-medium bg-ochre-soft border border-ochre-line text-ochre">
                        {invite.role}
                      </span>
                      {isOwner && (
                        <>
                          <button
                            type="button"
                            aria-label={`Resend invite to ${invite.invitedEmail}`}
                            disabled={resendInvite.isPending}
                            onClick={() =>
                              resendInvite.mutate(invite.id, {
                                onSuccess: () => setResentId(invite.id),
                              })
                            }
                            className="text-[11.5px] text-ink-2 hover:underline"
                          >
                            {resentId === invite.id ? 'Sent!' : 'Resend'}
                          </button>
                          <button
                            type="button"
                            aria-label={`Cancel invite to ${invite.invitedEmail}`}
                            disabled={cancelInvite.isPending}
                            onClick={() => cancelInvite.mutate(invite.id)}
                            className="text-[11.5px] text-danger hover:underline"
                          >
                            Cancel
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )}

            {members.length === 0 && (
              <div className="py-8 text-center text-[13px] text-muted">No team members yet.</div>
            )}
          </div>
        </AdminCard>

        {/* Invite form — owner only */}
        {isOwner && (
          <AdminCard title="Invite someone">
            <form onSubmit={handleInvite} className="flex flex-col gap-3">
              <p className="text-[12.5px] text-ink-2 m-0">
                Invite a coordinator or viewer to help manage your church's events and RSVPs.
              </p>

              {inviteSent && (
                <div className="text-[12.5px] text-good font-medium">Invite sent!</div>
              )}
              {inviteError && (
                <div role="alert" className="text-[12.5px] text-danger">{inviteError}</div>
              )}

              <FormField label="Email address" required>
                <TextInput
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="name@example.com"
                  required
                  aria-label="Invite email"
                />
              </FormField>

              <FormField label="Role">
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as 'coordinator' | 'viewer')}
                  aria-label="Invite role"
                  className="input h-[42px]"
                >
                  <option value="coordinator">Coordinator — can manage events and RSVPs</option>
                  <option value="viewer">Viewer — read-only access</option>
                </select>
              </FormField>

              <AdmBtn
                type="submit"
                variant="primary"
                disabled={createInvite.isPending || !inviteEmail.trim()}
                className="self-end"
              >
                {createInvite.isPending ? 'Sending…' : 'Send invite'}
              </AdmBtn>
            </form>
          </AdminCard>
        )}
      </div>
    </AdminShell>
  )
}
